import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormState } from 'react-final-form';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { reformatDateMMMMDDYYYY } from '../../../../utils';
import {
  minutesFromMidnightToReadableTime,
  weekDays as DAYS_OF_WEEK_FULL,
} from '../../../../utils/timeConverter';
import generateRecurringTwoWeekShiftDates from '../utils/recurringShiftDateGenerator';
import { calculateWeeksBetweenDates } from '../utils/recurringTwoWeeksHelpers';

import ConfirmModalFooter from './ConfirmModalSections/ConfirmModalFooter';
import JobDurationSection from './ConfirmModalSections/JobDurationSection';
import PositionDetailsSection from './ConfirmModalSections/PositionDetailsSection';
import SchedulePreviewSection from './ConfirmModalSections/SchedulePreviewSection';
import { confirmModalSx, typographySx } from './ConfirmModal.styles';
import {
  buildRecurringTwoWeeksApiPayload,
  buildSchedulePatternWeeks,
  computeHasInvalidShiftDuration,
  MAX_SHIFT_DURATION_MINUTES,
  MINUTES_IN_DAY,
} from './confirmModalUtils';

const ConfirmModal = ({ isOpen, onClose, onSubmit, isProcessing }) => {
  const smallScreen = useMediaQuery('(max-width:1100px)');
  const formState = useFormState();
  const currentFormValues = formState?.values;
  const translate = LanguageConverterV2;

  const dateRange = currentFormValues?.dateRange || [null, null];
  const [startDate, endDate] = dateRange;

  const week1Shifts = useMemo(
    () => currentFormValues?.week1Shifts ?? {},
    [currentFormValues?.week1Shifts]
  );
  const week2Shifts = useMemo(
    () => currentFormValues?.week2Shifts ?? {},
    [currentFormValues?.week2Shifts]
  );

  const hasInvalidShiftDuration = useMemo(
    () =>
      computeHasInvalidShiftDuration(week1Shifts, week2Shifts, {
        minutesInDay: MINUTES_IN_DAY,
        maxShiftDurationMinutes: MAX_SHIFT_DURATION_MINUTES,
      }),
    [week1Shifts, week2Shifts]
  );

  const weeks = useMemo(() => calculateWeeksBetweenDates(startDate, endDate), [startDate, endDate]);

  const formattedStartDate = useMemo(
    () => (startDate ? reformatDateMMMMDDYYYY(startDate) : ''),
    [startDate]
  );

  const formattedEndDate = useMemo(
    () => (endDate ? reformatDateMMMMDDYYYY(endDate) : ''),
    [endDate]
  );

  const patternWeeks = useMemo(
    () =>
      buildSchedulePatternWeeks({
        week1Shifts,
        week2Shifts,
        daysOfWeekFull: DAYS_OF_WEEK_FULL,
        minutesToReadableTime: minutesFromMidnightToReadableTime,
      }),
    [week1Shifts, week2Shifts]
  );

  // Generate recurring week dates with per-shift openings
  const generatedDates = useMemo(() => {
    try {
      return generateRecurringTwoWeekShiftDates({
        startDate,
        endDate,
        week1Shifts,
        week2Shifts,
        maxShiftDurationMinutes: MAX_SHIFT_DURATION_MINUTES,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating recurring dates:', error);
      return [];
    }
  }, [startDate, endDate, week1Shifts, week2Shifts]);

  const perTwoWeekCycleTotal = useMemo(
    () => (patternWeeks?.[0]?.totalOpenings || 0) + (patternWeeks?.[1]?.totalOpenings || 0),
    [patternWeeks]
  );

  const totalShiftsForJobDuration = useMemo(
    () => (generatedDates || []).reduce((sum, dateInfo) => sum + (dateInfo?.openings || 0), 0),
    [generatedDates]
  );

  const confirmationData = {
    position: currentFormValues?.positionTemplate?.name,
    location: currentFormValues?.location?.address,
    department: currentFormValues?.department?.description,
    generatedDates,
  };

  // Convert generated dates to API payload format
  const convertToApiPayload = useMemo(() => {
    if (generatedDates.length === 0) return null;
    if (hasInvalidShiftDuration) return null;

    const positionTemplateId = currentFormValues?.positionTemplate?._id;
    const departmentId = currentFormValues?.department?._id;
    const locationId = currentFormValues?.location?._id;

    if (!positionTemplateId || !departmentId || !locationId) {
      return null;
    }
    return buildRecurringTwoWeeksApiPayload({
      generatedDates,
      positionTemplateId,
      departmentId,
      locationId,
      startDate,
      endDate,
      week1Shifts,
      week2Shifts,
    });
  }, [
    currentFormValues,
    endDate,
    generatedDates,
    hasInvalidShiftDuration,
    startDate,
    week1Shifts,
    week2Shifts,
  ]);

  const canPost = !!convertToApiPayload;
  const handlePost = () => canPost && onSubmit && onSubmit(convertToApiPayload);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={confirmModalSx.modal}
    >
      <Box sx={confirmModalSx.container(smallScreen)}>
        {/* Header */}
        <Box sx={confirmModalSx.header}>
          <Typography sx={{ ...typographySx.title, mb: 0 }}>
            {translate('job.create.confirmation.header')}
          </Typography>
          <IconButton aria-label="close" onClick={onClose} sx={confirmModalSx.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={confirmModalSx.divider} />

        {/* Body */}
        <Box sx={confirmModalSx.scrollArea}>
          {/* Position Details and Job Duration */}
          <Box sx={confirmModalSx.sectionSpacing}>
            <PositionDetailsSection
              position={confirmationData?.position}
              location={confirmationData?.location}
              department={confirmationData?.department}
              translate={translate}
              typographySx={typographySx}
            />
            <JobDurationSection
              startDate={startDate}
              endDate={endDate}
              formattedStartDate={formattedStartDate}
              formattedEndDate={formattedEndDate}
              weeks={weeks}
              translate={translate}
              typographySx={typographySx}
            />
          </Box>

          {/* Generated Dates */}
          {generatedDates.length > 0 && (
            <SchedulePreviewSection
              patternWeeks={patternWeeks}
              perTwoWeekCycleTotal={perTwoWeekCycleTotal}
              totalShiftsForJobDuration={totalShiftsForJobDuration}
              translate={translate}
              typographySx={typographySx}
              sx={confirmModalSx}
            />
          )}

          {/* Empty state */}
          {generatedDates.length === 0 && (
            <Box sx={confirmModalSx.emptyState}>
              <Typography sx={typographySx.text} color="text.secondary">
                {translate('job.create.recurringTwoWeeks.confirm.noScheduleToView')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={confirmModalSx.divider} />
        <Box sx={confirmModalSx.footer}>
          <ConfirmModalFooter
            hasInvalidShiftDuration={hasInvalidShiftDuration}
            isProcessing={isProcessing}
            canPost={canPost}
            onClose={onClose}
            onPost={handlePost}
            translate={translate}
            sx={confirmModalSx}
          />
        </Box>
      </Box>
    </Modal>
  );
};

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default ConfirmModal;
